import React from "react";
import { useParams, Link } from "react-router-dom";
import { useSeo } from "../lib/useSeo";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";
import ArticlePage, { Article } from "./ArticlePage";

// NC foreclosure glossary: a hub (/glossary) + one indexable page per term
// (/glossary/:slug). Each term page reuses ArticlePage. Targets "what is
// [term]" searches and builds topical authority + internal links. Cross-links
// to the guides, calculator, and how-to articles.

interface GlossaryEntry {
  slug: string;
  term: string;
  short: string; // one-line summary for the hub
  seoTitle: string;
  seoDescription: string;
  body: React.ReactNode; // full definition for the term page
}

const def = (slug: string, term: string, short: string, body: React.ReactNode): GlossaryEntry => ({
  slug,
  term,
  short,
  seoTitle: `${term} — NC Foreclosure Glossary | Parcely`,
  seoDescription: `${term}: ${short}`,
  body: (
    <>
      <h1>{term}</h1>
      <p className="legal-updated">North Carolina foreclosure glossary</p>
      {body}
      <p>
        <Link to="/glossary" className="legal-back" style={{ display: "inline-block" }}>
          ← All foreclosure terms
        </Link>
      </p>
    </>
  ),
});

export const GLOSSARY: GlossaryEntry[] = [
  def(
    "upset-bid",
    "Upset bid",
    "A raised bid filed during the 10-day window after an NC foreclosure sale.",
    <>
      <p>
        An upset bid is a higher bid filed with the Clerk of Superior Court during the
        10-day window after a North Carolina foreclosure or tax sale is reported. To qualify,
        it must raise the standing bid by the greater of 5% or $750, and the bidder posts a
        deposit. Each valid upset bid restarts a fresh 10-day period, so a property can stay
        open for weeks.
      </p>
      <p>
        Run the numbers with the{" "}
        <a href="/upset-bid-calculator">upset bid calculator</a>.
      </p>
    </>
  ),
  def(
    "in-rem-foreclosure",
    "In rem foreclosure",
    "A streamlined NC tax foreclosure (§105-375) against the property itself.",
    <>
      <p>
        An in rem foreclosure under N.C.G.S. §105-375 is a faster, cheaper tax-foreclosure
        method where the county dockets a judgment directly against the property — "in rem"
        means against the thing, not a person — rather than filing a full civil lawsuit.
        It still ends in a public sale subject to upset bids. Firms like Kania Law handle
        many of these statewide.
      </p>
    </>
  ),
  def(
    "notice-of-sale",
    "Notice of sale",
    "The published notice setting an NC foreclosure auction's date, time, and place.",
    <>
      <p>
        After a foreclosure judgment, a notice of sale is published (newspaper and posted at
        the courthouse) stating the date, time, place, and terms of the auction. It's the
        point at which a foreclosure becomes a concrete, dated opportunity you can plan
        around.
      </p>
    </>
  ),
  def(
    "redemption",
    "Redemption",
    "The owner's right to pay off and reclaim the property before the sale is confirmed.",
    <>
      <p>
        Redemption is the property owner's right to stop the foreclosure by paying the taxes,
        interest, and costs owed. In an NC tax foreclosure the owner can generally redeem up
        until the sale is confirmed — so even a winning bid isn't truly final until
        confirmation. (Federal tax liens carry their own 120-day redemption right after the
        sale.)
      </p>
    </>
  ),
  def(
    "lis-pendens",
    "Lis pendens",
    "A public notice that a lawsuit affecting a property has been filed.",
    <>
      <p>
        A lis pendens ("suit pending") is a notice filed in the public record warning that
        litigation — such as a foreclosure — affecting a specific property is underway. For
        investors it's one of the earliest signals that a property is heading toward a
        foreclosure sale. See{" "}
        <a href="/how-to-find-pre-foreclosures-charlotte">how to find pre-foreclosures</a>.
      </p>
    </>
  ),
  def(
    "opening-bid",
    "Opening bid",
    "The starting bid at an NC foreclosure auction, often the debt plus costs.",
    <>
      <p>
        The opening bid is the minimum starting bid at the auction, typically set at the
        amount owed (back taxes or the debt) plus costs. In tax foreclosures the opening bid
        is often far below market value — which is exactly why the upset-bid competition that
        follows can run the final price up significantly.
      </p>
    </>
  ),
  def(
    "commissioner",
    "Commissioner",
    "The court-appointed person who conducts an NC foreclosure sale.",
    <>
      <p>
        A commissioner is the person the court appoints to conduct the foreclosure sale,
        report the results to the court, and ultimately deliver the deed to the confirmed
        buyer. In a §105-374 tax foreclosure the commissioner runs the public auction on the
        courthouse steps.
      </p>
    </>
  ),
  def(
    "confirmation",
    "Confirmation of sale",
    "The court's final approval of an NC foreclosure sale after the upset period ends.",
    <>
      <p>
        Confirmation is the court's final sign-off on the sale, which happens once the 10-day
        upset bid period passes with no new bid. Only after confirmation does the buyer pay
        the balance, receive the deed, and truly own the property. Before confirmation the
        owner can still redeem.
      </p>
    </>
  ),
  def(
    "tax-lien",
    "Tax lien (North Carolina)",
    "The county's claim on a property for unpaid taxes — foreclosed, not sold, in NC.",
    <>
      <p>
        A tax lien is the county's legal claim against a property for unpaid property taxes.
        Unlike tax-lien-certificate states, North Carolina does not sell its tax liens to
        investors — it forecloses them in court and sells the property. See{" "}
        <a href="/tax-deed-vs-tax-lien-nc">tax lien vs tax deed vs NC tax foreclosure</a>.
      </p>
    </>
  ),
  def(
    "report-of-sale",
    "Report of sale",
    "The commissioner's filing that starts the 10-day upset bid clock.",
    <>
      <p>
        After the auction, the commissioner files a report of sale with the court. That
        filing starts the 10-day upset bid window. Knowing the report-of-sale date is how you
        calculate the deadline to upset or defend a bid — try the{" "}
        <a href="/upset-bid-calculator">upset bid calculator</a>.
      </p>
    </>
  ),
];

const BY_SLUG: Record<string, GlossaryEntry> = Object.fromEntries(GLOSSARY.map((e) => [e.slug, e]));

// /glossary — the hub
export const GlossaryHub: React.FC = () => {
  useSeo({
    title: "NC Foreclosure Glossary — Upset Bids, In Rem & More | Parcely",
    description:
      "Plain-English definitions of North Carolina foreclosure terms: upset bid, in rem foreclosure, notice of sale, redemption, lis pendens, and more.",
  });
  return (
    <>
      <PublicPageHeader />
      <div className="legal-page" id="main" tabIndex={-1}>
        <div className="legal-content">
          <h1>NC Foreclosure Glossary</h1>
          <p className="legal-updated">Plain-English definitions for NC tax-foreclosure investors</p>
          <p>
            The terms you'll run into when buying North Carolina tax foreclosures, defined
            in plain English. New to the process? Start with the{" "}
            <a href="/how-to-buy-tax-foreclosures-nc">how-to-buy guide</a>.
          </p>
          {GLOSSARY.map((e) => (
            <p key={e.slug}>
              <Link to={`/glossary/${e.slug}`}>
                <strong>{e.term}</strong>
              </Link>{" "}
              — {e.short}
            </p>
          ))}
        </div>
      </div>
      <PublicPageFooter />
    </>
  );
};

// /glossary/:slug — a single term
export const GlossaryTermPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const entry = slug ? BY_SLUG[slug] : undefined;

  if (!entry) {
    const notFound: Article = {
      seoTitle: "Term not found — NC Foreclosure Glossary | Parcely",
      seoDescription: "Browse the North Carolina foreclosure glossary.",
      body: (
        <>
          <h1>Term not found</h1>
          <p>
            We couldn't find that term. See the full{" "}
            <Link to="/glossary">NC foreclosure glossary</Link>.
          </p>
        </>
      ),
    };
    return <ArticlePage article={notFound} />;
  }

  return <ArticlePage article={{ seoTitle: entry.seoTitle, seoDescription: entry.seoDescription, body: entry.body }} />;
};
